import * as SVG from "@svgdotjs/svg.js"
import {
	CanvasController,
	ComponentSymbol,
	MainController,
	SnapPoint,
	SnapDragHandler,
	SnappingInfo,
	BooleanProperty,
	SliderProperty,
	SectionHeaderProperty,
	defaultStroke,
	SelectionController,
	PathComponent,
	ChoiceEntry,
	ChoiceProperty,
	EnumOption,
	PathSaveObject,
	SymbolOption,
	Variant,
	PathLabelable,
	InfoProperty,
	CircuitComponent,
	PropertyCategories,
	Nameable,
	TikzPathCommand,
	CircuitikzTo,
	SaveController,
	buildTikzStringFromPathCommand,
	Voltageable,
	Currentable,
	EnvironmentVariableController,
} from "../internal"
import { lineRectIntersection, pointInsideRect, selectedBoxWidth, selectionSize } from "../utils/selectionHelper"

//TODO: add "line join" to poles, i.e. dot (.): this is essentially just a square with side length = line width (chapter 6.4 in documentation)
export type PoleEntry = { key: string; name: string; shortcut: string }
export const poleChoices: PoleEntry[] = [
	{ key: "none", name: "none", shortcut: "" },
	{ key: "circ", name: "circ", shortcut: "*" },
	{ key: "ocirc", name: "ocirc", shortcut: "o" },
	{ key: "diamondpole", name: "diamond", shortcut: "d" },
	{ key: "odiamondpole", name: "odiamond", shortcut: null },
	{ key: "squarepole", name: "square", shortcut: null },
	{ key: "osquarepole", name: "osquare", shortcut: null },
]

export type Pole = {
	start?: string
	end?: string
}

export type PathSymbolSaveObject = PathSaveObject & {
	id: string
	scale?: SVG.Point
	options?: string[]
	poles?: Pole
}

export class PathSymbolComponent extends Currentable(Voltageable(PathLabelable(Nameable(PathComponent)))) {
	private static jsonID = "path"
	static {
		CircuitComponent.jsonSaveMap.set(PathSymbolComponent.jsonID, PathSymbolComponent)
	}
	public referenceSymbol: ComponentSymbol

	private startLine: SVG.Line
	private endLine: SVG.Line
	private startLineSelection: SVG.Line
	private endLineSelection: SVG.Line
	private selectionRect: SVG.Rect
	private dragStartLine: SVG.Line
	private dragEndLine: SVG.Line

	private poleStartElement: SVG.Element
	private poleEndElement: SVG.Element
	/**
	 * the vector in local coordinates from componentVariant.mid to the point where the first half of the path line should end
	 */
	private relSymbolStart: SVG.Point
	/**
	 * the vector in local coordinates from componentVariant.mid to the point where the second half of the path line should start
	 */
	private relSymbolEnd: SVG.Point

	private rotationDeg: number = 0

	private mirror: BooleanProperty
	private invert: BooleanProperty

	private poleStart: ChoiceProperty<PoleEntry>
	private poleEnd: ChoiceProperty<PoleEntry>

	protected scaleProperty: SliderProperty

	/**
	 * the scale of the component, i.e. how much it is scaled compared to the original symbol size. this also includes mirroring and inverting.
	 * It is greatly discouraged to use this property to scale the component, since an elegant solution in tikz does not exist for that.
	 */
	protected scaleState: SVG.Point

	protected optionProperties: Map<BooleanProperty, SymbolOption>
	protected optionEnumProperties: Map<ChoiceProperty<ChoiceEntry>, EnumOption>
	protected componentVariant: Variant

	private isBattery: boolean = false

	constructor(symbol: ComponentSymbol) {
		super()
		this.scaleState = new SVG.Point(1, 1)
		this.scaleProperty = new SliderProperty(
			"Scale",
			0.1,
			10,
			0.01,
			new SVG.Number(1),
			true,
			undefined,
			"manipulation:scale"
		)
		this.scaleProperty.addChangeListener((ev) => {
			this.scaleState = new SVG.Point(
				Math.sign(this.scaleState.x) * ev.value.value,
				Math.sign(this.scaleState.y) * ev.value.value
			)
			this.update()
			this.updatePoles()
		})
		this.properties.add(PropertyCategories.manipulation, this.scaleProperty)

		this.optionProperties = new Map()
		this.optionEnumProperties = new Map()

		if (symbol.componentClass == "batteries") {
			this.isBattery = true
		}

		this.properties.add(
			PropertyCategories.options,
			new SectionHeaderProperty("Poles", undefined, "options:poles_header")
		)
		this.poleStart = new ChoiceProperty<PoleEntry>(
			"Start",
			poleChoices,
			poleChoices[0],
			undefined,
			"options:poles_start"
		)
		this.poleEnd = new ChoiceProperty<PoleEntry>("End", poleChoices, poleChoices[0], undefined, "options:poles_end")
		this.poleStart.addChangeListener((ev) => {
			this.updatePoles()
		})
		this.poleEnd.addChangeListener((ev) => {
			this.updatePoles()
		})
		this.properties.add(PropertyCategories.options, this.poleStart)
		this.properties.add(PropertyCategories.options, this.poleEnd)

		if (symbol.possibleOptions.length > 0 || symbol.possibleEnumOptions.length > 0) {
			this.properties.add(
				PropertyCategories.options,
				new SectionHeaderProperty("Options", undefined, "options:header")
			)
			for (const option of symbol.possibleOptions) {
				const property = new BooleanProperty(
					option.displayName ?? option.name,
					false,
					false,
					undefined,
					"options:option" + option.name
				)
				property.addChangeListener((ev) => {
					this.updateOptions()
				})
				this.optionProperties.set(property, option)
				this.properties.add(PropertyCategories.options, property)
			}
			for (const enumOption of symbol.possibleEnumOptions) {
				let choices: ChoiceEntry[] = enumOption.selectNone ? [{ key: "-", name: "--default--" }] : []
				enumOption.options.forEach((option) => {
					choices.push({ key: option.name, name: option.displayName ?? option.name })
				})
				const property = new ChoiceProperty(
					enumOption.displayName,
					choices,
					choices[0],
					undefined,
					"options:enum_" + enumOption.displayName
				)

				property.addChangeListener((ev) => {
					this.updateOptions()
				})
				this.optionEnumProperties.set(property, enumOption)
				this.properties.add(PropertyCategories.options, property)
			}
		}

		this.componentVariant = symbol.getVariant(this.optionsFromProperties())

		this.componentVisualization = CanvasController.instance.canvas.use(this.componentVariant.symbol)
		this.componentVisualization.fill("none")
		this.componentVisualization.stroke(defaultStroke)
		this.componentVisualization.node.style.color = defaultStroke

		this.displayName = symbol.displayName
		this.referenceSymbol = symbol
		this.scaleState = new SVG.Point(1, 1)

		let startPinIndex = this.componentVariant.pins.findIndex((value) => value.name === "START")
		let endPinIndex = this.componentVariant.pins.findIndex((value) => value.name === "END")

		this.relSymbolStart = this.componentVariant.pins.at(startPinIndex).point
		this.relSymbolEnd = this.componentVariant.pins.at(endPinIndex).point

		this.startLine = CanvasController.instance.canvas
			.line()
			.fill("none")
			.stroke({ color: defaultStroke, width: 0.5 })
		this.endLine = this.startLine.clone(true)

		this.dragStartLine = CanvasController.instance.canvas
			.line()
			.fill("none")
			.stroke({ width: selectionSize, color: "transparent" })
		this.dragEndLine = this.dragStartLine.clone(true)

		this.selectionElement.remove()
		this.selectionElement = CanvasController.instance.canvas.group()
		this.startLineSelection = CanvasController.instance.canvas.line().fill("none")
		this.endLineSelection = CanvasController.instance.canvas.line().fill("none")
		this.selectionRect = CanvasController.instance.canvas.rect(0, 0).fill("none")
		this.selectionElement.add(this.selectionRect)
		this.selectionElement.add(this.startLineSelection)
		this.selectionElement.add(this.endLineSelection)

		this.selectionElement.hide()

		this.visualization.add(this.componentVisualization)
		this.visualization.add(this.startLine)
		this.visualization.add(this.endLine)
		this.visualization.add(this.dragStartLine)
		this.visualization.add(this.dragEndLine)
		this.visualization.hide()

		this.properties.add(
			PropertyCategories.manipulation,
			new SectionHeaderProperty("Symbol Orientation", undefined, "manipulation:header")
		)

		this.mirror = new BooleanProperty("Mirror", false, undefined, undefined, "manipulation:mirror")
		this.mirror.addChangeListener((ev) => {
			this.scaleState.y *= -1
			this.update()
		})
		this.properties.add(PropertyCategories.manipulation, this.mirror)

		this.invert = new BooleanProperty("Invert", false, undefined, undefined, "manipulation:invert")

		this.invert.addChangeListener((ev) => {
			this.scaleState.x =
				Math.abs(this.scaleState.x) * (this.invert.value ? -1 : 1) * this.voltageConventionInvert()
			this.update()
		})
		this.scaleState.x = Math.abs(this.scaleState.x) * (this.invert.value ? -1 : 1) * this.voltageConventionInvert()
		this.properties.add(PropertyCategories.manipulation, this.invert)

		this.addInfo()

		this.snappingPoints = [
			new SnapPoint(this, null, new SVG.Point(0, 0)),
			new SnapPoint(this, null, new SVG.Point(0, 0)),
			...this.componentVariant.pins
				.filter((_, index) => !(index == startPinIndex || index == endPinIndex))
				.map((pin) => new SnapPoint(this, pin.name, pin.point.add(this.componentVariant.mid))),
		]
	}

	private voltageConventionInvert() {
		let voltageConventionInvert = 1
		if (
			this.referenceSymbol.tikzName == "american voltage source" ||
			this.referenceSymbol.tikzName == "american controlled voltage source"
		) {
			// in american voltage sources, the internal + and - are inverted, i.e. the symbol is inverted, if the voltage convention is old or RP
			const globalSettings = EnvironmentVariableController.instance.getGlobalSettings()
			if (globalSettings.voltageConvention == "old" || globalSettings.voltageConvention == "RP") {
				voltageConventionInvert = -1
			}
		}
		return voltageConventionInvert
	}

	protected updateVoltageRender() {
		this.voltageArrowRendering?.remove()

		if (this.voltageLabel.value != "") {
			let onlyLabel =
				this.referenceSymbol.tikzName == "american voltage source" ||
				this.referenceSymbol.tikzName == "american controlled voltage source"
			let isEuropean = this.referenceSymbol.tikzName.includes("european")
			let isAmerican = this.referenceSymbol.tikzName.includes("american")
			let voltageArrow = this.generateVoltageArrow(
				this.referencePoints[0],
				this.referencePoints[1],
				this.componentVariant.mid.mul(-1),
				new SVG.Point(this.componentVariant.viewBox.x2, this.componentVariant.viewBox.y2).sub(
					this.componentVariant.mid
				),
				this.scaleState,
				{
					isOpen: false,
					sourceType: this.isBattery ? "isBattery" : this.referenceSymbol.source,
					componentStyle:
						onlyLabel ? "onlyLabel"
						: isEuropean ? "european"
						: isAmerican ? "american"
						: undefined,
				}
			)
			this.voltageArrowRendering = voltageArrow.arrow
			this.voltageRendering.add(this.voltageArrowRendering)

			const voltageLabelBbox = this.voltageLabelRendering.bbox()
			const voltageLabelReference = new SVG.Point(voltageLabelBbox.cx, voltageLabelBbox.cy).add(
				new SVG.Point(voltageLabelBbox.w / 2, voltageLabelBbox.h / 2).mul(voltageArrow.labelAnchorDir)
			)
			this.voltageLabelRendering.transform(
				new SVG.Matrix({ translate: voltageArrow.labelPos.sub(voltageLabelReference) })
			)
		}
	}

	protected updateCurrentRender(): void {
		this.currentArrowRendering?.remove()
		if (this.currentLabel.value != "") {
			let currentArrow = this.generateCurrentArrow(
				this.referencePoints[0],
				this.referencePoints[1],
				this.componentVariant.mid.mul(-1),
				new SVG.Point(this.componentVariant.viewBox.x2, this.componentVariant.viewBox.y2).sub(
					this.componentVariant.mid
				),
				this.scaleState,
				{ isVoltageSource: this.referenceSymbol.source === "isVoltage" }
			)
			this.currentArrowRendering = currentArrow.arrow
			this.currentRendering.add(this.currentArrowRendering)

			const currentLabelBbox = this.currentLabelRendering.bbox()
			const currentLabelReference = new SVG.Point(currentLabelBbox.cx, currentLabelBbox.cy).add(
				new SVG.Point(currentLabelBbox.w / 2, currentLabelBbox.h / 2).mul(currentArrow.labelAnchorDir)
			)
			this.currentLabelRendering.transform(
				new SVG.Matrix({ translate: currentArrow.labelPos.sub(currentLabelReference) })
			)
		}
	}

	protected addInfo() {
		this.properties.add(PropertyCategories.info, new SectionHeaderProperty("Info"))
		this.properties.add(PropertyCategories.info, new InfoProperty("ID", this.referenceSymbol.tikzName))
	}

	protected optionsFromProperties(): SymbolOption[] {
		const selectedOptions: SymbolOption[] = []
		this.optionProperties.forEach((option, property) => {
			if (property.value) {
				selectedOptions.push(option)
			}
		})
		this.optionEnumProperties.forEach((option, property) => {
			if (property.value.key != "-") {
				selectedOptions.push(
					option.options.find((o) => {
						return o.name == property.value.key
					})
				)
			}
		})
		return selectedOptions
	}

	protected setPropertiesFromOptions(options: SymbolOption[]) {
		this.optionProperties.forEach((value, property) => {
			if (options.find((op) => op.name == value.name)) {
				property.value = true
			} else {
				property.value = false
			}
		})
		this.optionEnumProperties.forEach((enumOption, property) => {
			let foundOption = false
			for (const option of enumOption.options) {
				if (options.find((op) => op.name == option.name)) {
					foundOption = true
					property.value = property.entries.find((entry) => entry.key == option.name)
					break
				}
			}
			if (!foundOption) {
				property.value = property.entries[0]
			}
		})
		this.updateOptions()
	}

	public moveStartTo(position: SVG.Point) {
		this.referencePoints[0] = position
		this.update()
	}

	public moveEndTo(position: SVG.Point) {
		this.referencePoints[1] = position
		this.update()
	}

	public flip(horizontal: boolean): void {
		let newPos1 = new SVG.Point(this.referencePoints[0].x, this.referencePoints[1].y)
		let newPos2 = new SVG.Point(this.referencePoints[1].x, this.referencePoints[0].y)
		if (horizontal) {
			this.referencePoints[0] = newPos1
			this.referencePoints[1] = newPos2
		} else {
			this.referencePoints[0] = newPos2
			this.referencePoints[1] = newPos1
		}
		this.mirror.updateValue(!this.mirror.value)

		this.update()
	}

	public recalculateSnappingPoints(): void {
		const inverseTransform = this.getTransformMatrix().inverse()
		this.snappingPoints[0].updateRelPosition(this.referencePoints[0].transform(inverseTransform))
		this.snappingPoints[1].updateRelPosition(this.referencePoints[1].transform(inverseTransform))
		super.recalculateSnappingPoints()
	}

	protected updateOptions(): void {
		this.componentVariant = this.referenceSymbol.getVariant(this.optionsFromProperties())
		this.componentVisualization.node.setAttribute("href", "#" + this.componentVariant.symbol.id())

		let startPinIndex = this.componentVariant.pins.findIndex((value) => value.name === "START")
		let endPinIndex = this.componentVariant.pins.findIndex((value) => value.name === "END")
		this.snappingPoints = [
			new SnapPoint(this, null, new SVG.Point(0, 0)),
			new SnapPoint(this, null, new SVG.Point(0, 0)),
			...this.componentVariant.pins
				.filter((_, index) => !(index == startPinIndex || index == endPinIndex))
				.map((pin) => new SnapPoint(this, pin.name, pin.point.add(this.componentVariant.mid))),
		]
		this.update()
	}
	private updatePoles() {
		this.poleStartElement?.remove()
		this.poleEndElement?.remove()
		this.poleStartElement = null
		this.poleEndElement = null

		if (this.poleStart.value.key != "none") {
			this.poleStartElement = CanvasController.instance.canvas.use("node_" + this.poleStart.value.key)
			this.visualization.add(this.poleStartElement)
		}

		if (this.poleEnd.value.key != "none") {
			this.poleEndElement = CanvasController.instance.canvas.use("node_" + this.poleEnd.value.key)
			this.visualization.add(this.poleEndElement)
		}

		this.updatePolePositions()
	}
	private updatePolePositions() {
		if (this.poleStartElement) {
			const bbox = this.poleStartElement.bbox()
			const point = new SVG.Point(bbox.w / 2, bbox.h / 2)
			this.poleStartElement.transform(
				new SVG.Matrix({
					scale: Math.abs(this.scaleState.x),
					translate: point.mul(-1),
					origin: point,
				}).lmultiply({ translate: this.referencePoints[0] })
			)
		}
		if (this.poleEndElement) {
			const bbox = this.poleEndElement.bbox()
			const point = new SVG.Point(bbox.w / 2, bbox.h / 2)
			this.poleEndElement.transform(
				new SVG.Matrix({
					scale: Math.abs(this.scaleState.x),
					translate: point.mul(-1),
					origin: point,
				}).lmultiply({ translate: this.referencePoints[1] })
			)
		}
	}

	public getSnappingInfo(): SnappingInfo {
		if (this.finishedPlacing) {
			return {
				trackedSnappingPoints: this.snappingPoints,
				additionalSnappingPoints: [],
			}
		} else {
			return {
				trackedSnappingPoints: [],
				additionalSnappingPoints:
					this.referencePoints.length > 0 ? [new SnapPoint(this, "", new SVG.Point())] : [],
			}
		}
	}

	public update(): void {
		this.position = this.referencePoints[0].add(this.referencePoints[1]).div(2)

		const angle = Math.atan2(
			this.referencePoints[0].y - this.referencePoints[1].y,
			this.referencePoints[1].x - this.referencePoints[0].x
		)
		this.rotationDeg = (angle * 180) / Math.PI

		let voltageConventionInvert = this.voltageConventionInvert() < 0
		this.scaleState.x = Math.abs(this.scaleState.x) * (this.invert.value != voltageConventionInvert ? -1 : 1)

		let m = this.getTransformMatrix()
		this.componentVisualization.transform(m)

		let startLineEndPoint = this.relSymbolStart.add(this.componentVariant.mid).transform(m)
		let endLineStartPoint = this.relSymbolEnd.add(this.componentVariant.mid).transform(m)

		if (this.invert.value != voltageConventionInvert) {
			let switchPos = startLineEndPoint
			startLineEndPoint = endLineStartPoint
			endLineStartPoint = switchPos
		}

		this.recalculateResizePoints()
		this.startLine.plot(
			this.referencePoints[0].x,
			this.referencePoints[0].y,
			startLineEndPoint.x,
			startLineEndPoint.y
		)
		this.endLine.plot(
			this.referencePoints[1].x,
			this.referencePoints[1].y,
			endLineStartPoint.x,
			endLineStartPoint.y
		)
		this.startLineSelection.plot(
			this.referencePoints[0].x,
			this.referencePoints[0].y,
			startLineEndPoint.x,
			startLineEndPoint.y
		)
		this.endLineSelection.plot(
			this.referencePoints[1].x,
			this.referencePoints[1].y,
			endLineStartPoint.x,
			endLineStartPoint.y
		)
		this.dragStartLine.plot(
			this.referencePoints[0].x,
			this.referencePoints[0].y,
			startLineEndPoint.x,
			startLineEndPoint.y
		)
		this.dragEndLine.plot(
			this.referencePoints[1].x,
			this.referencePoints[1].y,
			endLineStartPoint.x,
			endLineStartPoint.y
		)

		this.updatePolePositions()

		this.updatePathLabel()
		this.updateVoltageRender()
		this.updateCurrentRender()
		this._bbox = this.visualization.bbox()
		this.referencePosition = this.position.sub(new SVG.Point(this._bbox.x, this._bbox.y))

		this.recalculateSelectionVisuals()
		this.recalculateSnappingPoints()
	}

	protected recalculateSelectionVisuals(): void {
		if (this.selectionElement) {
			// use the saved position instead of the bounding box (bbox position fails in safari)
			let bbox = this.componentVariant.viewBox
			this.selectionRect
				.center(bbox.cx, bbox.cy)
				.size(bbox.w + selectedBoxWidth, bbox.h + selectedBoxWidth)
				.transform(this.getTransformMatrix())
		}
	}

	public viewSelected(show: boolean): void {
		super.viewSelected(show)
		this.resizable(this.isSelected && show && SelectionController.instance.currentlySelectedComponents.length == 1)
	}

	/**
	 * For tikz path symbols, this getTransformMatrix returns the transformation necessary for the symbol from local symbol coodinates to world coordinates
	 */
	public getTransformMatrix(): SVG.Matrix {
		const symbolRel = this.componentVariant.mid
		return new SVG.Matrix({
			scaleX: this.scaleState.x,
			scaleY: this.scaleState.y,
			translate: [-symbolRel.x, -symbolRel.y],
			origin: [symbolRel.x, symbolRel.y],
		}).lmultiply(
			new SVG.Matrix({
				rotate: -this.rotationDeg,
				translate: [this.position.x, this.position.y],
			})
		)
	}

	public updateTheme() {
		let labelColor = defaultStroke
		if (this.labelColor && this.labelColor.value) {
			labelColor = this.labelColor.value.toString()
		}
		this.labelRendering?.fill(labelColor)
	}

	public isInsideSelectionRectangle(selectionRectangle: SVG.Box): boolean {
		// if 1 of the 2 lines hanging of the symbol intersect the selection rect -> should select
		if (
			lineRectIntersection(this.startLine, selectionRectangle) ||
			lineRectIntersection(this.endLine, selectionRectangle)
		) {
			return true
		}

		const bbox = this.componentVariant.viewBox
		// get the corner points of the bounding box and rotate each of them to their proper positions
		const transform = this.getTransformMatrix()
		const boxPoints = [
			new SVG.Point(bbox.x, bbox.y).transform(transform),
			new SVG.Point(bbox.x2, bbox.y).transform(transform),
			new SVG.Point(bbox.x2, bbox.y2).transform(transform),
			new SVG.Point(bbox.x, bbox.y2).transform(transform),
		]

		// if all of these points are inside the selection rect -> should select
		if (boxPoints.map((value) => pointInsideRect(value, selectionRectangle)).every((value) => value)) {
			return true
		}

		//necessary to check if the complete selection rect is inside the component
		let selectionRectInside = true

		// if at least one line defined by 2 of the 4 corner points intersects the selection rect -> should select
		for (let index = 0; index < boxPoints.length; index++) {
			const p1 = boxPoints[index]
			const p2 = boxPoints[(index + 1) % boxPoints.length]
			if (
				lineRectIntersection(
					[
						[p1.x, p1.y],
						[p2.x, p2.y],
					],
					selectionRectangle
				)
			) {
				return true
			} else {
				selectionRectInside =
					selectionRectInside &&
					p2.sub(p1).rotate(-90).dot(new SVG.Point(selectionRectangle.cx, selectionRectangle.cy).sub(p1)) > 0
			}
		}

		// no intersection between the selection rect and the component or selection rect inside component
		return selectionRectInside
	}

	public toJson(): PathSymbolSaveObject {
		let data = super.toJson() as PathSymbolSaveObject
		data.type = PathSymbolComponent.jsonID
		data.id = this.referenceSymbol.tikzName

		if (this.componentVariant.options.length > 0) {
			data.options = this.componentVariant.options.map((option) => option.displayName ?? option.name)
		}

		if (this.scaleState) {
			let scaleSave = this.scaleState.clone()
			scaleSave.x *= this.voltageConventionInvert()
			if (scaleSave && (scaleSave.x != 1 || scaleSave.y != 1)) {
				data.scale = scaleSave
			}
		}

		if (this.poleStartElement || this.poleEndElement) {
			const poles: Pole = {
				start: this.poleStart.value.key != "none" ? this.poleStart.value.key : undefined,
				end: this.poleEnd.value.key != "none" ? this.poleEnd.value.key : undefined,
			}
			data.poles = poles
		}

		return data
	}

	public toTikzString(): string {
		let command: TikzPathCommand = {
			options: ["draw"],
			additionalNodes: [],
			coordinates: [],
			connectors: [],
		}
		this.buildTikzCommand(command)
		return buildTikzStringFromPathCommand(command)
	}

	protected buildTikzCommand(command: TikzPathCommand): void {
		super.buildTikzCommand(command)
		let options: string[] = [this.referenceSymbol.tikzName]
		options.push(...this.referenceSymbol.optionsToStringArray(this.optionsFromProperties()))
		if (this.mirror.value) {
			options.push("mirror")
		}
		if (this.invert.value) {
			options.push("invert")
		}

		if (this.poleStartElement || this.poleEndElement) {
			if (this.poleStart.value.shortcut != null && this.poleEnd.value.shortcut != null) {
				options.push(this.poleStart.value.shortcut + "-" + this.poleEnd.value.shortcut)
			} else {
				options.push("bipole nodes={" + this.poleStart.value.key + "}{" + this.poleEnd.value.key + "}")
			}
		}

		const scaleFactor =
			this.scaleProperty.value.value != 1 ? new SVG.Number(this.scaleProperty.value.value * 1.4, "cm") : undefined
		if (scaleFactor) {
			options.push("/tikz/circuitikz/bipoles/length=" + scaleFactor.value.toPrecision(3) + scaleFactor.unit)
		}

		let to: CircuitikzTo = { options: options, name: this.name.value }
		this.buildTikzPathLabel(to)
		this.buildTikzVoltage(to)
		this.buildTikzCurrent(to)
		command.connectors.push(to)
	}

	public remove(): void {
		SnapDragHandler.snapDrag(this, false)
		this.resizable(false)
		this.viewSelected(false)
		this.visualization.remove()
		this.selectionElement?.remove()
		this.labelRendering?.remove()
	}

	public copyForPlacement(): PathSymbolComponent {
		return new PathSymbolComponent(this.referenceSymbol)
	}

	protected static idNoOptions(id: string): string {
		return id.split("_").slice(0, 2).join("_")
	}

	public applyJson(saveObject: PathSymbolSaveObject): void {
		super.applyJson(saveObject)
		let options = saveObject.options ?? []
		this.setPropertiesFromOptions(this.referenceSymbol.getOptionsFromOptionNames(options))

		if (saveObject.scale) {
			this.scaleState = new SVG.Point(saveObject.scale)
		}
		this.mirror.value = this.scaleState.y < 0
		this.invert.value = this.scaleState.x * this.voltageConventionInvert() < 0
		this.scaleProperty.value = new SVG.Number(Math.abs(this.scaleState.x))

		if (saveObject.poles) {
			if (saveObject.poles.start != undefined) {
				this.poleStart.value = poleChoices.find((choice) => choice.key == saveObject.poles.start)
			}
			if (saveObject.poles.end != undefined) {
				this.poleEnd.value = poleChoices.find((choice) => choice.key == saveObject.poles.end)
			}
		}

		this.update()
		this.visualization.show()
		this.updatePoles()
	}

	public static fromJson(saveObject: PathSymbolSaveObject): PathSymbolComponent {
		let symbol: ComponentSymbol

		if (SaveController.instance.currentlyLoadedSaveVersion != "") {
			symbol = MainController.instance.symbols.find((symbol) => symbol.tikzName == saveObject.id)
		} else {
			let idParts = saveObject.id.split("_")
			symbol = MainController.instance.symbols.find(
				(symbol) => symbol.tikzName == idParts[1].replaceAll("-", " ")
			)
			saveObject.options = idParts.slice(2)
			// @ts-ignore
			saveObject.points = [saveObject.start, saveObject.end]
		}
		if (symbol) {
			let pathComponent: PathSymbolComponent = new PathSymbolComponent(symbol)
			return pathComponent
		} else {
			console.error("no path symbol found for saveObject: " + JSON.stringify(saveObject))
			return null
		}
	}

	public updatePathLabel(): void {
		if (!this.mathJaxLabel || !this.labelRendering) {
			return
		}
		let labelSVG = this.labelRendering
		// breaking points where the label is parallel to the path or to the x axis. in degrees
		let breakVertical = 70
		let breakHorizontal = 21

		const globalSettings = EnvironmentVariableController.instance.getGlobalSettings().labelOrientation

		let pathDiff = this.referencePoints[1].sub(this.referencePoints[0])

		// the bounding boxes for the label and the symbol
		let labelBBox = labelSVG.bbox()
		let labelHalfSize = new SVG.Point(labelBBox.w, labelBBox.h).div(2)
		let symbolBBox = this.componentVisualization.bbox()
		let symbolHalfSize = new SVG.Point(symbolBBox.w, symbolBBox.h).div(2)

		// the nominal reference point of the label (bottom center)
		let labelRef = new SVG.Point(labelBBox.cx, labelBBox.y2)
		// the rotation angle of the label (not always identical to the path rotation angle)
		let rotAngle = this.rotationDeg
		if (rotAngle > 90 || rotAngle < -90) {
			// left halfplane -> don't show label upside down -> rotate the label by additional 180 deg
			rotAngle += 180
			// the dafault label reference point should now be the top center
			labelRef.y = labelBBox.y
		}

		// mirroring the symbol should not impact the label except from shifting its position to stay close to the symbol (only relevant for asymetric symbols)
		let referenceoffsetY =
			this.scaleState.y < 0 ? this.componentVariant.mid.y - symbolBBox.h : -this.componentVariant.mid.y
		referenceoffsetY *= Math.abs(this.scaleState.y)

		// nominally the reference point of the symbol is its center (w.r.t. the x coordinate for a path which is horizontal)
		let referenceOffsetX = 0

		let other = this.labelSide.value ? -1 : 1
		if (other < 0) {
			labelRef.y = labelBBox.y - labelRef.y
			referenceoffsetY += symbolBBox.h * Math.abs(this.scaleState.y)
		}

		if (globalSettings != "rotate") {
			if (globalSettings == "straight") {
				breakHorizontal = 46
				breakVertical = 46
			}

			// if the path is close to horizontal or vertical according to the break points
			let nearHorizontal =
				Math.abs(this.rotationDeg) < breakHorizontal || Math.abs(this.rotationDeg) > 180 - breakHorizontal
			let nearVertical =
				Math.abs(this.rotationDeg) > breakVertical && Math.abs(this.rotationDeg) < 180 - breakVertical

			if (nearHorizontal) {
				// the label should not be rotated w.r.t. the x axis
				rotAngle = 0
				let right = Math.sign(pathDiff.x)
				let up = Math.sign(this.rotationDeg)
				//the offset where the rotation pivot point should lie (for both label and symbol)
				let horizontalOffset = Math.min(labelHalfSize.x, symbolHalfSize.x) * up
				referenceOffsetX = horizontalOffset * right * other
				labelRef.x += horizontalOffset * other
			} else if (nearVertical) {
				// the label should not be rotated w.r.t. the x axis
				rotAngle = 0
				let right = Math.sign(pathDiff.x)
				let up = Math.sign(this.rotationDeg)
				//the offset where the rotation pivot point should lie (for both label and symbol)
				let verticalOffset = Math.min(labelHalfSize.y, symbolHalfSize.x) * right * other

				referenceOffsetX = -verticalOffset * up

				labelRef.y = labelBBox.cy + verticalOffset
				labelRef.x += labelHalfSize.x * (up * other)
			}
		}

		referenceoffsetY -= other * (this.labelDistance.value ? this.labelDistance.value.convertToUnit("px").value : 0)

		// where the anchor point of the symbol is located relative to the midAbs point
		let referenceOffset = new SVG.Point(referenceOffsetX, referenceoffsetY).transform(
			new SVG.Matrix({
				rotate: -this.rotationDeg,
			})
		)

		// actually move and rotate the label to the correct position
		let compRef = this.position.add(referenceOffset)
		let movePos = compRef.sub(labelRef)
		labelSVG.transform({
			rotate: -rotAngle,
			ox: labelRef.x,
			oy: labelRef.y,
			translate: [movePos.x, movePos.y],
		})
	}
}
